import { useEffect, useState } from "react";
import {
  DOMAINS, FLAG_LABELS, FRAMEWORKS, JURISDICTIONS, JURISDICTION_NAMES, LIBRARY_VERSION, ROLE_OPTIONS, SOURCES, STAGES, US_STATES, applicableControls, controlDrivers, coverage, frameworkCoverage,
  changesSince, formatDate, isoDate, obligationState, obligations, type Action, type ControlStatusValue, type Jurisdiction, type SystemProfile, type UsState,
} from "@aigov/domain";
import { api, ApiError, type SystemDto, type SystemInput } from "../api";
import { Bar, ErrorNote, Pill, errMsg } from "../ui";

interface Props {
  tenantId: string;
  systems: SystemDto[];
  selected: string | null;
  onSelect: (id: string) => void;
  allowed: (a: Action) => boolean;
  toast: (m: string) => void;
  onChange: (s: SystemDto) => void;
  onDeleted: (id: string) => void;
  onConflict: () => void;
}

const STATUS: [ControlStatusValue, string][] = [["none", "Not started"], ["partial", "In progress"], ["done", "In place"], ["na", "Not applicable"]];
const ORDER: Record<Jurisdiction, number> = { EU: 0, UK: 1, AU: 2, US: 3 };

export function SystemView(p: Props) {
  const s = p.systems.find((x) => x.id === p.selected) ?? p.systems[0];
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => { setEditing(false); setConfirmDelete(false); }, [s?.id]);

  if (!s) return <div className="panel empty"><strong>No system selected.</strong><span className="muted">Add a system on the Register tab.</span></div>;

  const profile: SystemProfile = { role: s.role, jurisdictions: s.jurisdictions, domain: s.domain, flags: s.flags, usStates: s.usStates };
  const asOf = isoDate();
  const obs = obligations(profile).sort((a, b) => ORDER[a.jurisdiction] - ORDER[b.jurisdiction]);
  const ctls = applicableControls(profile);
  const drivers = controlDrivers(profile);
  const cv = coverage(profile, s.controls);
  const fwc = frameworkCoverage(profile, s.controls);
  const canWrite = p.allowed("system.write");
  const canControl = p.allowed("control.write");

  const setControl = async (cid: string, patch: { status?: ControlStatusValue; evidence?: string }) => {
    const cur = s.controls[cid];
    const body = { status: patch.status ?? cur?.status ?? "none", evidence: patch.evidence ?? cur?.evidence ?? "" };
    try { p.onChange(await api<SystemDto>("PUT", `/tenants/${p.tenantId}/systems/${s.id}/controls/${cid}`, body)); p.toast("Saved"); }
    catch (e) { p.toast(errMsg(e)); }
  };

  return (
    <div className="stack">
      <div className="row spread">
        <label className="row small muted">System
          <select className="btn" id="pick" value={s.id} onChange={(e) => p.onSelect(e.target.value)}>
            {p.systems.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        <div className="row"><Pill tone={s.summary.tier.tone}>{s.summary.tier.label}</Pill>
          {canWrite && <button className="btn" onClick={() => setEditing((v) => !v)}>{editing ? "Close profile" : "Edit profile"}</button>}</div>
      </div>

      {s.summary.stale && (
        <div className="note">
          <strong>Assessed against library v{s.libraryVersion}; the current library is v{LIBRARY_VERSION}.</strong> Review what changed, then save the profile to re-baseline.
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {changesSince(s.libraryVersion, profile).map(({ release, affects, controls }) => (
              <li key={release.version}><span className="mono">v{release.version}</span> ({formatDate(release.date)}): {release.summary}
                {affects.length > 0 ? <> <strong>Affects this system:</strong> {affects.map((id) => obs.find((o) => o.id === id)?.title ?? id).join("; ")}.</>
                  : controls.length ? null : <span className="muted"> No obligations of this system changed.</span>}
                {controls.filter((c) => ctls.some((x) => x.id === c)).length > 0 && <> Control {controls.join(", ")} now applies.</>}</li>))}
          </ul>
        </div>)}

      {editing ? (
        <ProfileForm s={s} tenantId={p.tenantId} onSaved={(x) => { p.onChange(x); setEditing(false); p.toast("Profile saved"); }} onConflict={p.onConflict} />
      ) : (
        <section className="panel">
          <h2>{s.name}</h2>
          <p className="muted" style={{ margin: "4px 0 0" }}>{s.description || "No description yet."}</p>
          <div className="row small" style={{ marginTop: 8 }}>
            {ROLE_OPTIONS.find((r) => r.value === s.role)?.label} · {DOMAINS.find((d) => d[0] === s.domain)?.[1]} · {s.stage}{s.ownerName && ` · Owner: ${s.ownerName}`}
            {s.jurisdictions.map((j) => <span key={j} className="chip">{j}</span>)}
            {s.jurisdictions.includes("US") && <span className="muted">US states: {s.usStates.length ? s.usStates.join(", ") : "none selected"}</span>}
          </div>
        </section>
      )}

      <div className="grid2">
        <section className="panel">
          <div className="row spread"><h2>Applicable obligations</h2><span className="muted small">{obs.length} items</span></div>
          <p className="muted small" style={{ margin: "4px 0 0" }}>Indicative, based on the profile above. Not legal advice: confirm applicability with qualified counsel.</p>
          <div style={{ marginTop: 6 }}>
            {obs.length ? obs.map((o) => {
              const st = obligationState(o, asOf);
              return (
                <div className="ob" key={o.id}>
                  <span className="jur">{o.jurisdiction}</span>
                  <div><div className="t">{o.title}</div><div className="mono muted">{o.basis}</div></div>
                  <Pill tone={st.tone}>{st.label}</Pill>
                  <div className="d">{o.detail} <a href={SOURCES[o.source].url} target="_blank" rel="noopener noreferrer">Source</a>
                    {o.confidence === "M" && <> · <span className="vdot v-M" />verify detail</>}</div>
                </div>);
            }) : <p className="muted">Select at least one jurisdiction in the profile.</p>}
          </div>
        </section>
        <section className="panel">
          <h2>Control coverage</h2>
          <div className="row" style={{ margin: "10px 0 14px" }}>
            <b style={{ font: "600 30px/1 var(--f-display)" }}>{cv.pct}%</b>
            <span className="muted small">{cv.done} of {cv.applicable} controls in place; in progress counts half</span>
          </div>
          <p className="muted small" style={{ margin: "0 0 10px" }}>A self-assessment of your own records. Not evidence of conformity, certification or compliance.</p>
          <table className="small"><tbody>
            {fwc.map((x) => <tr key={x.framework}><td>{FRAMEWORKS.find((f) => f.key === x.framework)?.name}</td>
              <td style={{ width: "45%" }}><Bar pct={x.coverage.pct} /></td><td style={{ width: 44 }}>{x.coverage.pct}%</td></tr>)}
          </tbody></table>
        </section>
      </div>

      <section className="panel">
        <div className="row spread"><h2>Controls for this system</h2><span className="muted small">{ctls.length} applicable</span></div>
        {ctls.map((c) => {
          const st = s.controls[c.id];
          return (
            <div className="ctl" key={c.id}>
              <span className="mono" style={{ paddingTop: 2 }}>{c.id}</span>
              <div><div style={{ fontWeight: 600 }}>{c.title}</div><div className="muted small">{c.description}</div>
                <div className="why">{drivers[c.id]?.length ? `Required by: ${drivers[c.id]!.join(" · ")}` : "Baseline control"}</div></div>
              <select className="input" id={`st-${c.id}`} aria-label={`Status for ${c.id}`} disabled={!canControl}
                value={st?.status ?? "none"} onChange={(e) => setControl(c.id, { status: e.target.value as ControlStatusValue })}>
                {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <textarea className="input" id={`ev-${c.id}`} aria-label={`Evidence for ${c.id}`} disabled={!canControl}
                placeholder="Evidence: document, owner, link" defaultValue={st?.evidence ?? ""} key={`${c.id}-${st?.updatedAt ?? ""}`}
                onBlur={(e) => { if (e.target.value.trim() !== (st?.evidence ?? "")) void setControl(c.id, { evidence: e.target.value }); }} />
            </div>);
        })}
      </section>

      {p.allowed("system.delete") && (
        <div className="row">
          {!confirmDelete ? <button className="btn ghost danger" onClick={() => setConfirmDelete(true)}>Delete this system</button> : (
            <span className="row small">Delete {s.name} and its control records? The audit trail keeps the history.
              <button className="btn danger" onClick={async () => {
                try { await api("DELETE", `/tenants/${p.tenantId}/systems/${s.id}`); p.onDeleted(s.id); } catch (e) { p.toast(errMsg(e)); }
              }}>Delete</button>
              <button className="btn ghost" onClick={() => setConfirmDelete(false)}>Keep</button></span>)}
        </div>
      )}
    </div>
  );
}

function ProfileForm({ s, tenantId, onSaved, onConflict }: { s: SystemDto; tenantId: string; onSaved: (s: SystemDto) => void; onConflict: () => void }) {
  const [f, setF] = useState<SystemInput>({ name: s.name, ownerName: s.ownerName, description: s.description, role: s.role,
    jurisdictions: s.jurisdictions, usStates: s.usStates, domain: s.domain, stage: s.stage, flags: { ...s.flags } });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof SystemInput>(k: K, v: SystemInput[K]) => setF((x) => ({ ...x, [k]: v }));

  return (
    <section className="panel">
      <form className="grid" onSubmit={async (e) => {
        e.preventDefault(); setBusy(true); setError(null);
        try { onSaved(await api<SystemDto>("PUT", `/tenants/${tenantId}/systems/${s.id}`, { ...f, version: s.version })); }
        catch (err) { setError(errMsg(err)); if (err instanceof ApiError && err.status === 409) onConflict(); }
        finally { setBusy(false); }
      }}>
        <label className="f">System name<input id="p-name" required value={f.name} onChange={(e) => set("name", e.target.value)} /></label>
        <label className="f">Accountable owner<input id="p-owner" value={f.ownerName} onChange={(e) => set("ownerName", e.target.value)} /></label>
        <fieldset className="full"><legend>Who built it and who uses it</legend>
          <div style={{ display: "grid", gap: 8 }}>
            {ROLE_OPTIONS.map((r) => (
              <label key={r.value} style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", gap: "2px 8px", alignItems: "start", fontSize: 13 }}>
                <input type="radio" name="p-role" id={`role-${r.value}`} checked={f.role === r.value} onChange={() => set("role", r.value)} style={{ marginTop: 3 }} />
                <span><strong style={{ fontWeight: 600 }}>{r.label}</strong><br /><span className="muted small">{r.help}</span></span>
              </label>))}
          </div>
        </fieldset>
        <label className="f">Lifecycle stage<select id="p-stage" value={f.stage} onChange={(e) => set("stage", e.target.value as SystemInput["stage"])}>
          {STAGES.map((x) => <option key={x}>{x}</option>)}</select></label>
        <label className="f full">Use-case domain<select id="p-domain" value={f.domain} onChange={(e) => set("domain", e.target.value as SystemInput["domain"])}>
          {DOMAINS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label className="f full">Description<textarea id="p-desc" rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} /></label>
        <fieldset className="full"><legend>Jurisdictions where it is used or placed on the market</legend><div className="checks">
          {JURISDICTIONS.map((j) => <label key={j}><input type="checkbox" id={`j-${j}`} checked={f.jurisdictions.includes(j)}
            onChange={(e) => set("jurisdictions", e.target.checked ? [...f.jurisdictions, j] : f.jurisdictions.filter((x) => x !== j))} /> {JURISDICTION_NAMES[j]}</label>)}
        </div></fieldset>
        {f.jurisdictions.includes("US") && (
          <fieldset className="full"><legend>US states where it is used (most US AI law is state law)</legend><div className="checks">
            {US_STATES.map(([k, l]) => <label key={k}><input type="checkbox" id={`us-${k}`} checked={f.usStates.includes(k)}
              onChange={(e) => set("usStates", e.target.checked ? [...f.usStates, k] : f.usStates.filter((x: UsState) => x !== k))} /> {l}</label>)}
          </div><span className="muted small">States not listed have no AI-specific law in this library yet; federal law still applies.</span></fieldset>
        )}
        <fieldset className="full"><legend>Characteristics</legend><div className="checks">
          {FLAG_LABELS.map(([k, l]) => <label key={k}><input type="checkbox" id={`fl-${k}`} checked={!!f.flags[k]}
            onChange={(e) => set("flags", { ...f.flags, [k]: e.target.checked })} /> {l}</label>)}
        </div></fieldset>
        <div className="full"><ErrorNote error={error} /></div>
        <div className="row full"><button className="btn primary" disabled={busy}>Save profile</button>
          <span className="muted small">Saving re-baselines the system on library v{LIBRARY_VERSION}.</span></div>
      </form>
    </section>
  );
}
